<template>
  <div>
    <div
      v-if="!(isAddSubMode || isEditSubMode)"
      class="parties-of-lawsuit-selection text-center"
    >
      <h2 class="parties-heading">
        {{ local.lawsuitRequest.step2.head1Prosecuters }}<span v-if="showDefendantList"> {{ local.common.and }} {{ local.lawsuitRequest.step2.head1Defendants }}</span>
      </h2>
      <p class="parties-description">
        {{ local.lawsuitRequest.step2.head2Prosecuters }}<span v-if="showDefendantList"> {{ local.common.and }} {{ local.lawsuitRequest.step2.head2Defendants }}</span> {{ local.lawsuitRequest.step2.head2Common }}
      </p>
      <v-row class="all-parties-lists">
        <v-col
          cols="12"
          lg="6"
        >
          <naj-input
            v-if="isEditMode && !isInternalLawsuit && isNotCompleted"
            :type-id="formFieldType.checkbox"
            :value.sync="auditFields.isProsecutorListAudited"
            :label="
              `${local.requestReply.checklist.title} ${requestReplyChecklist.isProsecutorListAudited}`
            "
            :disabled="isAuditFieldsDisabled"
            class="inline-label primary-label-color"
            @change="
              updateAuditFields({ isProsecutorListAudited: auditFields.isProsecutorListAudited })
            "
          ></naj-input>
          <case-parties-list
            :type="casePartyRoleEnum.prosecutor"
            :section-title="local.common.theProsecutorList"
            :data="prosecutors"
            @addCaseParty="onOpenAddCaseParty({ casePartyRole: casePartyRoleEnum.prosecutor })"
            @update-case-party="onOpenUpdateCaseParty"
            @remove-case-party="onOpenRemoveCaseParty"
            @update-case-party-representative="onOpenUpdateCasePartyRepresentative"
            @remove-case-party-representative="onOpenRemoveCasePartyRepresentative"
          ></case-parties-list>
        </v-col>

        <v-col
          v-if="showDefendantList"
          cols="12"
          lg="6"
          :class="{'audit-field-margin-top': (selectedCaseType && selectedCaseType.mustHaveNoDefendant)}"
        >
          <naj-input
            v-if="isEditMode && !isInternalLawsuit && isNotCompleted && selectedCaseType && !selectedCaseType.mustHaveNoDefendant"
            :type-id="formFieldType.checkbox"
            :value.sync="auditFields.isDefendantListAudited"
            :label="
              `${local.requestReply.checklist.title} ${requestReplyChecklist.isDefendantListAudited}`
            "
            :disabled="isAuditFieldsDisabled"
            class="inline-label primary-label-color"
            @change="
              updateAuditFields({ isDefendantListAudited: auditFields.isDefendantListAudited })
            "
          ></naj-input>
          <case-parties-list
            :type="casePartyRoleEnum.defendant"
            :section-title="local.common.theDefendantList"
            :data="defendants"
            @addCaseParty="onOpenAddCaseParty({ casePartyRole: casePartyRoleEnum.defendant })"
            @update-case-party="onOpenUpdateCaseParty"
            @remove-case-party="onOpenRemoveCaseParty"
            @update-case-party-representative="onOpenUpdateCasePartyRepresentative"
            @remove-case-party-representative="onOpenRemoveCasePartyRepresentative"
          ></case-parties-list>
        </v-col>

        <v-row>
          <v-col
            cols="12"
            class="text-left"
          >
            <naj-button
              outlined
              @click="stepBack"
            >
              {{ local.common.back }}
            </naj-button>
            <naj-button @click="save">
              {{ local.common.next }}
            </naj-button>
          </v-col>
        </v-row>
      </v-row>

      <add-current-user-as-case-party
        :add-current-user-as-case-party.sync="addCurrentUserAsCasePartyPopup"
        @save="onSaveCurrentUserAsOneOfCaseParties"
        @canceled="() => {
          addCurrentUserAsCasePartyPopup = false;
          openAddCaseParty({ casePartyRole: casePartyRoleEnum.prosecutor });
        }"
      ></add-current-user-as-case-party>

      <naj-confirmation
        :show-modal="showRemovePopup"
        :title="local.common.removeConfirmation"
        :message="removeMessage"
        :ok-btn-label="local.common.remove"
        :action-loading="removeLoading"
        color="error"
        @ok="removeConfirmed"
        @cancel="removeCanceled"
      ></naj-confirmation>
    </div>

    <div v-else>
      <case-party-form :check-commercial-register-number-status="true"></case-party-form>
    </div>
  </div>
</template>

<script>
import CasePartyForm from './case-party-form';
import CasePartiesList from './case-parties-list';
import AddCurrentUserAsCaseParty from './add-current-user-as-case-party/index';
import { isProsecutor, isDefendant, isProsecutorOrDefendant } from '../../helpers/utils';
import { casePartyRoleEnum, casePartyRoleByIdEnum, requestReplyChecklist, userType } from '@/helpers/enum.js';
import { createNamespacedHelpers } from 'vuex';
import { getMissingMandatoryRoles } from '@/modules/lawsuit/helpers/get-missing-mandatory-roles.js';
const { mapState: mapGlobalState } = createNamespacedHelpers('GlobalModule');
const { mapState, mapActions, mapGetters } = createNamespacedHelpers('LawsuitModule');

export default {
  components: {
    CasePartyForm,
    CasePartiesList,
    AddCurrentUserAsCaseParty,
  },
  data() {
    return {
      casePartyRoleEnum,
      isProsecutor,
      isDefendant,
      isProsecutorOrDefendant,
      addCurrentUserAsCasePartyPopup: false,
      showRemovePopup: false,
      isCasePartyRepresentative: false,
      removeLoading: false,
      requestReplyChecklist,
    };
  },
  computed: {
    ...mapGlobalState(['userInfo']),
    ...mapState({ auditFields: state => state.lawsuitRequest.auditFields }),
    ...mapGetters([
      'isViewMode',
      'isNewMode',
      'isEditMode',
      'isAddSubMode',
      'isEditSubMode',
      'isNotCompleted',
      'isCurrentUserExists',
      'prosecutors',
      'defendants',
      'prosecutorsLength',
      'selectedCaseParty',
      'selectedCasePartyRelatedParty',
      'selectedCasePartyRepresentativesLength',
      'isInternalLawsuit',
      'isAuditFieldsDisabled',
      'selectedCaseType',
    ]),
    removeMessage() {
      if (!this.selectedCaseParty) return '';

      if (!this.isCasePartyRepresentative) {
        const casePartyRoleName = this.local.common.casePartyRoles[
          casePartyRoleByIdEnum[this.selectedCaseParty.casePartyRole]
        ];
        const casePartyRepresentativesLength = this.selectedCasePartyRepresentativesLength;
        if (casePartyRepresentativesLength >= 1) {
          return `لا يمكن استرجاع ال${casePartyRoleName} وممثليه بعد حذفهم. هل ترغب بتأكيد حذف ال${casePartyRoleName} وممثليه؟`;
        } else {
          return this.local.common.dynamicRemoveConfirmationMessage(
            this.local.common.the + casePartyRoleName
          );
        }
      } else {
        const relatedCaseParty = this.selectedCasePartyRelatedParty;
        if (relatedCaseParty && relatedCaseParty.casePartyRole) {
          const relatedCasePartyRoleName = this.local.common.casePartyRoles[
            casePartyRoleByIdEnum[relatedCaseParty.casePartyRole]
          ];
          return `لا يمكن استرجاع ممثل ال${relatedCasePartyRoleName} بعد حذفه. هل ترغب بتأكيد حذف ممثل ال${relatedCasePartyRoleName}؟`;
        } else {
          return `لا يمكن استرجاع الممثل بعد حذفه. هل ترغب بتأكيد حذف الممثل؟`;
        }
      }
    },
    nextButtonLabel() {
      if (this.isViewMode || this.isEditMode) {
        return this.local.common.next;
      }
      if (this.isNewMode) {
        return this.local.common.saveAndContinue;
      }
      return '';
    },
    showDefendantList() {
      return (this.defendants && Object.values(this.defendants).length > 0) || (this.selectedCaseType && !this.selectedCaseType.mustHaveNoDefendant);
    },
  },
  methods: {
    ...mapActions({
      save: 'stepCasePartiesNext',
      stepBack: 'stepBack',
      onSaveCurrentUserAsOneOfCaseParties: 'saveCurrentUserAsOneOfCaseParties',
      openAddCaseParty: 'openAddCaseParty',
      onOpenUpdateCaseParty: 'openUpdateCaseParty',
      onOpenUpdateCasePartyRepresentative: 'openUpdateCasePartyRepresentative',
      openRemoveCaseParty: 'openRemoveCaseParty',
      openRemoveCasePartyRepresentative: 'openRemoveCasePartyRepresentative',
      removeCaseParty: 'removeCaseParty',
      removeCasePartyRepresentative: 'removeCasePartyRepresentative',
      removeCasePartyCanceled: 'removeCasePartyCanceled',
      updateAuditFields: 'updateAuditFields',
      getActivePartyAttributes: 'getActivePartyAttributes',
    }),

    validateMandatoryRoles({ caseTypeId, legalPositionId, caseParties, partyAttributes }) {
      return getPartyAttribuites(partyAttributes, {
        caseTypeId,
        legalPositionId,
        partyTypeId: null,
        caseParties,
        legalPositionForValidation: legalPositionId,
      });
    },
    async onOpenAddCaseParty(caseParty) {
      const isOrganization = (this.userInfo || {}).userType == userType.najizOrganization;
      if (this.isNewMode && this.isProsecutor(caseParty) && !isOrganization && !this.isCurrentUserExists && this.prosecutorsLength <= 0) {
        this.addCurrentUserAsCasePartyPopup = true;
        return;
      }

      if (this.selectedCaseType && !this.selectedCaseType.mustHaveNoDefendant && this.isNewMode && this.isDefendant(caseParty) && this.prosecutorsLength <= 0) {
        this.toaster.error('يرجى إضافة المدعي اولاً');
        return;
      }

      if (this.isDefendant(caseParty) && this.selectedCaseType?.id) {
        const partyAttributesForProsecutor = await this.getActivePartyAttributes({
          caseTypeId: this.selectedCaseType.id,
          casePartyRole: this.casePartyRoleEnum.prosecutor,
        });

        const missingMandatoryRoles = getMissingMandatoryRoles(partyAttributesForProsecutor, {
          caseTypeId: this.selectedCaseType.id,
          legalPositionId: this.casePartyRoleEnum.prosecutor,
          caseParties: Object.values(this.prosecutors),
        });

        if (missingMandatoryRoles && missingMandatoryRoles.length > 0) {
          this.toaster.error('يجب اضافة جميع المدعين الالزاميين');
          return;
        }
      }

      if (this.isProsecutor(caseParty) && (this.defendants && Object.values(this.defendants).length > 0) && this.selectedCaseType?.id) {
        const partyAttributesForDefendant = await this.getActivePartyAttributes({
          caseTypeId: this.selectedCaseType.id,
          casePartyRole: this.casePartyRoleEnum.defendant
        });
          const missingMandatoryRoles = getMissingMandatoryRoles(partyAttributesForDefendant, {
          caseTypeId: this.selectedCaseType.id,
          legalPositionId: this.casePartyRoleEnum.defendant,
          caseParties: Object.values(this.defendants),
        });

        if (missingMandatoryRoles && missingMandatoryRoles.length > 0) {
          this.toaster.error('يجب اضافة جميع المدعى عليهم الالزاميين');
          return;
        }
   
      }

      this.openAddCaseParty(caseParty);
    },

    onOpenRemoveCaseParty(caseParty) {
      this.isCasePartyRepresentative = false;
      this.openRemoveCaseParty(caseParty)
        .then(() => (this.showRemovePopup = true))
        .catch(() => (this.showRemovePopup = false));
    },
    onOpenRemoveCasePartyRepresentative(casePartyRepresentative) {
      this.isCasePartyRepresentative = true;
      this.openRemoveCasePartyRepresentative(casePartyRepresentative)
        .then(() => (this.showRemovePopup = true))
        .catch(() => (this.showRemovePopup = false));
    },
    removeConfirmed() {
      this.removeLoading = true;
      if (this.isCasePartyRepresentative) {
        this.removeCasePartyRepresentative()
          .finally(() => {
            this.removeLoading = false;
            this.showRemovePopup = false;
          });
      } else {
        this.removeCaseParty()
          .finally(() => {
            this.removeLoading = false;
            this.showRemovePopup = false;
          });
      }
    },
    removeCanceled() {
      this.showRemovePopup = false;
      this.removeLoading = false;
      this.removeCasePartyCanceled();
    },
  },
};
</script>

<style lang="scss" scoped>
.all-parties-lists {
  .audit-field-margin-top {
    margin-top: 22px;
  }
}
.component-actions {
  margin-top: 15px;
}
</style>
